import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  getAdminLoans,
  approveAdminLoan,
  rejectAdminLoan,
  forceCloseAdminLoan,
  updateAdminLoanNotes,
} from '../services/loanService';
import { AdminEmptyState, AdminFilterBar, AdminPageHeader, useAdminFeedback } from '../components/admin/primitives';

const statusColor = (status) => {
  if (status === 'closed') return 'success';
  if (status === 'overdue') return 'error';
  if (status === 'pending') return 'info';
  if (status === 'rejected') return 'default';
  return 'warning';
};

const AdminLoans = () => {
  const { showError, showSuccess, Feedback } = useAdminFeedback();
  const [status, setStatus] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [notes, setNotes] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const data = await getAdminLoans({ status: status || undefined });
      setItems(data.items || []);
    } catch (e) {
      showError(e?.response?.data?.message || 'Failed to load loans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [status]);

  const openDialog = (action, loan) => {
    setNotes(loan.adminNotes || '');
    setDialog({ action, loan });
  };

  const closeDialog = () => {
    setDialog(null);
    setNotes('');
  };

  const submitDialog = async () => {
    if (!dialog?.loan) return;
    const { action, loan } = dialog;
    try {
      setBusyId(loan._id);
      if (action === 'approve') await approveAdminLoan(loan._id, { notes });
      else if (action === 'reject') await rejectAdminLoan(loan._id, { notes });
      else if (action === 'force-close') await forceCloseAdminLoan(loan._id, { notes });
      else if (action === 'notes') await updateAdminLoanNotes(loan._id, { notes });
      showSuccess(
        action === 'approve' ? 'Loan approved and disbursed'
          : action === 'reject' ? 'Loan rejected'
            : action === 'force-close' ? 'Loan force-closed'
              : 'Notes saved'
      );
      closeDialog();
      await load();
    } catch (e) {
      showError(e?.response?.data?.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const dialogTitle = {
    approve: 'Approve loan',
    reject: 'Reject loan',
    'force-close': 'Force-close loan',
    notes: 'Edit notes',
  }[dialog?.action] || 'Loan action';

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <AdminPageHeader
        description="Review pending loan requests, approve or reject, and manage active fleet loans."
        sx={{ mb: 1 }}
      />

      <AdminFilterBar>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="overdue">Overdue</MenuItem>
          <MenuItem value="closed">Closed</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
        </TextField>
      </AdminFilterBar>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Card>
        <CardContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Loan</TableCell>
                <TableCell>Rider</TableCell>
                <TableCell>Principal</TableCell>
                <TableCell>EMI</TableCell>
                <TableCell>Outstanding</TableCell>
                <TableCell>Next Due</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((loan) => (
                <TableRow key={loan._id}>
                  <TableCell>{loan.loanNumber || loan._id}</TableCell>
                  <TableCell>{loan.riderId?.name || loan.riderId?.tmpIngameName || 'N/A'}</TableCell>
                  <TableCell>{loan.principal}</TableCell>
                  <TableCell>{loan.emiAmount}</TableCell>
                  <TableCell>{loan.outstandingAmount}</TableCell>
                  <TableCell>{loan.nextDueDate ? new Date(loan.nextDueDate).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={loan.status} color={statusColor(loan.status)} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>
                    <Typography variant="caption" color="text.secondary" noWrap title={loan.adminNotes || ''}>
                      {loan.adminNotes || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                      {loan.status === 'pending' && (
                        <>
                          <Button size="small" variant="contained" disabled={busyId === loan._id} onClick={() => openDialog('approve', loan)}>
                            Approve
                          </Button>
                          <Button size="small" color="error" disabled={busyId === loan._id} onClick={() => openDialog('reject', loan)}>
                            Reject
                          </Button>
                        </>
                      )}
                      {['active', 'overdue'].includes(loan.status) && (
                        <Button size="small" color="warning" disabled={busyId === loan._id} onClick={() => openDialog('force-close', loan)}>
                          Force close
                        </Button>
                      )}
                      <Button size="small" disabled={busyId === loan._id} onClick={() => openDialog('notes', loan)}>
                        Notes
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={9} sx={{ border: 0 }}>
                    <AdminEmptyState title="No loans found" description="Try a different status filter." />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">Showing {items.length} loans</Typography>
      </Box>

      <Dialog open={Boolean(dialog)} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {dialog?.loan?.loanNumber} · {dialog?.loan?.riderId?.name || dialog?.loan?.riderId?.tmpIngameName || 'Rider'}
          </Typography>
          <TextField
            label="Admin notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            placeholder="Optional notes for the rider / audit trail"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={submitDialog} disabled={Boolean(busyId)}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Feedback />
    </Container>
  );
};

export default AdminLoans;
